import React from "react";
import { resolveAvatarUrl, illustratedUrl, renderUserAvatar } from "@/lib/avatar";
export { renderUserAvatar };

/**
 * Consistent avatar component with fallback illustration + status ring.
 *
 * Resolution order (single source of truth via /lib/avatar.js):
 *   1) avatar_type=photo → uploaded profile_photo_url
 *   2) avatar_type=illustrated → DiceBear built from avatar_config + seed
 *   3) Legacy avatar_url (external URL) if present
 *   4) DiceBear notionists-neutral fallback (seed = email/id/name)
 */
const STATUS_COLORS = {
  available: "#10B981",
  busy: "#EF4444",
  meeting: "#8B5CF6",
  focus: "#6366F1",
  lunch: "#F59E0B",
  away: "#F59E0B",
  offline: "#9CA3AF",
};
const STATUS_LABELS = {
  available: "Available",
  busy: "Busy",
  meeting: "In a meeting",
  focus: "Focus mode",
  lunch: "Lunch",
  away: "Away",
  offline: "Offline",
};
const MEETING_STATUSES = new Set(["meeting"]);

const SIZE_MAP = {
  xs: 20, sm: 24, md: 32, lg: 40, xl: 56, xxl: 72,
};
const RING_MAP = {
  xs: 1.5, sm: 1.5, md: 2, lg: 2, xl: 2.5, xxl: 3,
};

export default function Avatar({
  user,
  size = "md",
  status = null, // "available" | ... | null (unspecified)
  online = null,
  showStatus = true,
  meetingBadge = false,
  ring = false,
  muted = false,
  className = "",
  onClick = null,
  title = null,
  "data-testid": testId,
}) {
  const dim = SIZE_MAP[size] || 32;
  const ringPx = RING_MAP[size] || 2;
  // Offline flag is authoritative — it means the user has no live WS session.
  // In that case, ignore any DB-stored `status` and force the offline dot so
  // the UI never claims someone is available when they're not connected.
  const forcedOffline = online === false;
  const effectiveStatus = forcedOffline
    ? "offline"
    : (status || user?.status || "available");
  const isOffline = effectiveStatus === "offline" || forcedOffline;
  const src = resolveAvatarUrl(user);
  const label = STATUS_LABELS[effectiveStatus] || "";
  const bg = STATUS_COLORS[effectiveStatus] || STATUS_COLORS.offline;

  return (
    <div
      onClick={onClick}
      title={title || (user?.name ? `${user.name} — ${label}` : label)}
      className={`relative shrink-0 inline-block ${onClick ? "cursor-pointer" : ""} ${className}`}
      style={{ width: dim, height: dim }}
      data-testid={testId}
    >
      <div
        className="w-full h-full rounded-full overflow-hidden bg-zinc-100"
        style={{
          boxShadow: ring ? `0 0 0 ${ringPx}px #fff, 0 0 0 ${ringPx + 2}px ${bg}` : undefined,
          filter: muted || isOffline ? "grayscale(0.85) opacity(0.85)" : undefined,
        }}
      >
        <img src={src} alt={user?.name || ""} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.src = illustratedUrl(user); }}/>
      </div>
      {showStatus && (
        <span
          aria-label={label}
          className="absolute rounded-full"
          style={{
            right: -1, bottom: -1,
            width: Math.max(8, dim * 0.28), height: Math.max(8, dim * 0.28),
            background: bg,
            border: `${ringPx}px solid white`,
          }}
        />
      )}
      {meetingBadge && MEETING_STATUSES.has(effectiveStatus) && (
        <span className="absolute -top-1 -right-1 text-[9px] font-semibold px-1 py-0.5 rounded-full bg-purple-500 text-white uppercase tracking-widest">Mtg</span>
      )}
    </div>
  );
}

export { STATUS_COLORS, STATUS_LABELS };
